// Custom 3D Viewer with AR System - Generic Container Support
class Custom3DViewer {
  constructor(container, modelUrl, product, options = {}) {
    this.container = container;
    this.modelUrl = modelUrl;
    this.product = product;
    this.options = {
      canvasSelector: '.viewer-3d-canvas',
      loadingSelector: '.viewer-3d-loading',
      arButtonSelector: '.custom-ar-button',
      ...options
    };
    
    // Generic container support - try multiple selector patterns
    this.canvas = container.querySelector(this.options.canvasSelector) ||
                  container.querySelector('.product-modal__3d-canvas') ||
                  container.querySelector('.3d-viewer-canvas') ||
                  container.querySelector('canvas');
    
    this.loadingElement = container.querySelector(this.options.loadingSelector) ||
                          container.querySelector('.product-modal__3d-loading') ||
                          container.querySelector('.3d-viewer-loading') ||
                          container.querySelector('.viewer-3d-loading');
    
    this.scene = null;
    this.camera = null;
    this.renderer = null;
    this.model = null;
    this.animationId = null;
    
    this.init();
  }

  async init() {
    try {
      // Check if Three.js is available, if not wait for threeJSReady event
      if (!window.THREE || !window.GLTFLoader) {
        console.log('📦 Waiting for THREE.js libraries...');
        await this.waitForThreeJS();
      }
      
      this.setupScene();
      await this.loadModel();
      this.animate();
      
      // Hide loading
      if (this.loadingElement) {
        this.loadingElement.style.display = 'none';
      }
      
      // Add custom AR button with suffix-based detection
      this.addCustomARButton();
      
    } catch (error) {
      console.error('🚫 3D Viewer Error:', error);
      this.showError(error.message);
    }
  }

  addCustomARButton() {
    // Find AR models by suffix
    const arModels = this.getARModels();
    
    if (!arModels.android && !arModels.ios) {
      return;
    }
    
    // Create AR button with generic class
    const arButton = document.createElement('button');
    arButton.type = 'button';
    arButton.className = 'custom-ar-button';
    arButton.innerHTML = '📱 Ortamında Gör';
    
    // Style button for 3D viewer overlay
    arButton.style.cssText = `
      position: absolute !important;
      bottom: 16px !important;
      right: 16px !important;
      background: rgba(0, 0, 0, 0.9) !important;
      color: white !important;
      border: none !important;
      border-radius: 20px !important;
      padding: 12px 20px !important;
      font-size: 14px !important;
      font-weight: 600 !important;
      cursor: pointer !important;
      z-index: 999999 !important;
      backdrop-filter: blur(12px) !important;
      transition: all 0.3s ease !important;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.6) !important;
      min-height: 44px !important;
      min-width: 140px !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      font-family: -apple-system, BlinkMacSystemFont, sans-serif !important;
      pointer-events: auto !important;
    `;
    
    // Hover effects
    arButton.addEventListener('mouseenter', () => {
      arButton.style.background = 'rgba(0, 0, 0, 0.95)';
      arButton.style.transform = 'scale(1.05)';
    });
    
    arButton.addEventListener('mouseleave', () => {
      arButton.style.background = 'rgba(0, 0, 0, 0.9)';
      arButton.style.transform = 'scale(1)';
    });
    
    // AR launch handler
    arButton.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.launchAR(arModels);
    });
    
    // Add button to container
    this.container.style.position = 'relative';
    this.container.appendChild(arButton);
  }

  getARModels() {
    const arModels = { android: null, ios: null };
    
    if (this.product?.media) {
      this.product.media.forEach(media => {
        if (media.media_type === 'model' && media.sources?.[0]?.url) {
          const url = media.sources[0].url;
          
          if (url.includes('_yat.glb')) {
            arModels.android = media;
          } else if (url.includes('_yat.usdz')) {
            arModels.ios = media;
          }
        }
      });
    }
    
    return arModels;
  }

  launchAR(arModels) {
    // Platform detection
    const userAgent = navigator.userAgent;
    const isMobile = /iPhone|iPad|iPod|Android/i.test(userAgent);
    const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
    const isAndroid = /Android/i.test(userAgent);
    
    if (!isMobile) {
      this.showQRModal();
      return;
    }
    
    if (isIOS && arModels.ios) {
      this.launchIOSAR(arModels.ios.sources[0].url);
    } else if (isAndroid && arModels.android) {
      this.launchAndroidAR(arModels.android.sources[0].url);
    } else {
      if (isIOS && arModels.android) {
        window.open(arModels.android.sources[0].url, '_blank');
      } else if (isAndroid && arModels.ios) {
        window.open(arModels.ios.sources[0].url, '_blank');
      } else {
        alert('Bu ürün için AR modeli bulunamadı.');
      }
    }
  }

  launchIOSAR(usdzUrl) {
    try {
      const testLink = document.createElement('a');
      const supportsAR = testLink.relList && testLink.relList.supports('ar');
      
      if (!supportsAR) {
        console.warn('⚠️ AR Quick Look not supported');
        window.open(usdzUrl, '_blank');
        return;
      }
      
      const arLink = document.createElement('a');
      arLink.href = usdzUrl;
      arLink.setAttribute('rel', 'ar');
      
      const previewImg = document.createElement('img');
      const productPreview = this.product?.featured_image || 
                           this.product?.media?.[0]?.preview_image?.src;
      
      if (productPreview) {
        previewImg.src = productPreview;
      } else {
        previewImg.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200"><rect width="200" height="200" fill="%23f8f9fa" stroke="%23dee2e6" stroke-width="2"/><text x="100" y="100" text-anchor="middle" dy="0.3em" fill="%236c757d" font-size="16">📖 AR Model</text></svg>';
      }
      
      previewImg.alt = `${this.product.title} AR Model`;
      previewImg.style.cssText = 'width: 200px; height: 200px; border-radius: 12px; object-fit: cover;';
      
      arLink.appendChild(previewImg);
      arLink.style.cssText = 'position: fixed; top: -9999px; left: -9999px; opacity: 0; pointer-events: none;';
      
      document.body.appendChild(arLink);
      
      setTimeout(() => {
        const clickEvent = new MouseEvent('click', {
          bubbles: true,
          cancelable: true,
          view: window
        });
        
        arLink.dispatchEvent(clickEvent);
        
        setTimeout(() => {
          if (arLink.parentNode) arLink.remove();
        }, 100);
      }, 10);
      
    } catch (error) {
      console.error('❌ iOS AR trigger failed:', error);
      window.open(usdzUrl, '_blank');
    }
  }

  launchAndroidAR(glbUrl) {
    try {
      const sceneViewerUrl = `intent://arvr.google.com/scene-viewer/1.0?file=${encodeURIComponent(glbUrl)}&mode=ar_only#Intent;scheme=https;package=com.google.ar.core;action=android.intent.action.VIEW;S.browser_fallback_url=https://developers.google.com/ar;end;`;
      
      window.location.href = sceneViewerUrl;
      
    } catch (error) {
      console.error('❌ Android AR launch failed:', error);
      window.open(glbUrl, '_blank');
    }
  }

  showQRModal() {
    try {
      const productHandle = this.product?.handle || 'defter';
      const arLauncherUrl = `/products/${productHandle}?view=ar`;
      const currentUrl = window.location.origin + arLauncherUrl;
      
      const logoUrl = 'https://cdn.shopify.com/s/files/1/0901/0738/0008/files/baska-qr-logo.png';
      const qrApiUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(currentUrl)}&logo=${encodeURIComponent(logoUrl)}&logo_size=60x60&ecc=M&margin=20&format=png`;
      
      const qrOverlay = document.createElement('div');
      qrOverlay.className = 'qr-modal-overlay';
      qrOverlay.style.cssText = `
        position: fixed !important;
        top: 0 !important;
        left: 0 !important;
        width: 100% !important;
        height: 100% !important;
        background: rgba(0, 0, 0, 0.8) !important;
        z-index: 999999 !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        backdrop-filter: blur(8px) !important;
        opacity: 0 !important;
        transition: opacity 0.3s ease !important;
      `;
      
      const qrModal = document.createElement('div');
      qrModal.className = 'qr-modal-content';
      qrModal.style.cssText = `
        background: white !important;
        border-radius: 20px !important;
        padding: 40px !important;
        text-align: center !important;
        max-width: 400px !important;
        width: 90% !important;
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3) !important;
        transform: scale(0.8) !important;
        transition: transform 0.3s ease !important;
      `;
      
      qrModal.innerHTML = `
        <div style="margin-bottom: 20px;">
          <h3 style="margin: 0 0 10px 0; font-size: 24px; color: #1a1a1a; font-family: -apple-system, BlinkMacSystemFont, sans-serif;">
            📱 Mobil Cihazında AR'da Gör
          </h3>
          <p style="margin: 0; font-size: 16px; color: #666; line-height: 1.4;">
            QR kodu telefonunla tarayarak<br>ürünü AR ortamında görüntüle
          </p>
        </div>
        
        <div style="margin: 30px 0; position: relative;">
          <img src="${qrApiUrl}" alt="QR Code" style="width: 300px; height: 300px; border-radius: 16px; box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);">
          <div style="position: absolute; bottom: -10px; right: -10px; background: #FF6B35; color: white; padding: 8px 12px; border-radius: 12px; font-size: 12px; font-weight: 600; box-shadow: 0 4px 12px rgba(255, 107, 53, 0.3);">
            📖 ${this.product?.title?.substring(0, 20)}${(this.product?.title?.length > 20) ? '...' : ''}
          </div>
        </div>
        
        <div style="margin-bottom: 30px;">
          <p style="margin: 0 0 15px 0; font-size: 14px; color: #888; line-height: 1.5;">
            <strong>📱 iPhone:</strong> Kamera açıp QR'ı tara<br>
            <strong>🤖 Android:</strong> Google Lens veya kamera kullan
          </p>
          <div style="display: flex; justify-content: center; gap: 10px; margin-top: 15px;">
            <span style="background: #f0f0f0; padding: 6px 12px; border-radius: 8px; font-size: 12px; color: #666;">✅ iOS AR Quick Look</span>
            <span style="background: #f0f0f0; padding: 6px 12px; border-radius: 8px; font-size: 12px; color: #666;">✅ Android Scene Viewer</span>
          </div>
        </div>
        
        <button class="qr-close-btn" style="
          background: #FF6B35 !important;
          color: white !important;
          border: none !important;
          padding: 12px 30px !important;
          border-radius: 12px !important;
          font-size: 16px !important;
          font-weight: 600 !important;
          cursor: pointer !important;
          font-family: -apple-system, BlinkMacSystemFont, sans-serif !important;
        ">
          Kapat
        </button>
      `;
      
      const closeBtn = qrModal.querySelector('.qr-close-btn');
      const closeModal = () => {
        qrOverlay.style.opacity = '0';
        qrModal.style.transform = 'scale(0.8)';
        setTimeout(() => {
          if (qrOverlay.parentNode) qrOverlay.remove();
        }, 300);
      };
      
      closeBtn.addEventListener('click', closeModal);
      qrOverlay.addEventListener('click', (e) => {
        if (e.target === qrOverlay) closeModal();
      });
      
      const handleEsc = (e) => {
        if (e.key === 'Escape') {
          closeModal();
          document.removeEventListener('keydown', handleEsc);
        }
      };
      document.addEventListener('keydown', handleEsc);
      
      qrOverlay.appendChild(qrModal);
      document.body.appendChild(qrOverlay);
      
      requestAnimationFrame(() => {
        qrOverlay.style.opacity = '1';
        qrModal.style.transform = 'scale(1)';
      });
      
    } catch (error) {
      console.error('❌ QR modal creation failed:', error);
      alert('AR özelliği için bu sayfayı mobil cihazınızda açın:\n\n' + window.location.href);
    }
  }

  async waitForThreeJS() {
    return new Promise((resolve, reject) => {
      // If already loaded, resolve immediately
      if (window.THREE && window.GLTFLoader) {
        resolve();
        return;
      }
      
      // Set up event listener for threeJSReady
      const handleThreeJSReady = () => {
        if (window.THREE && window.GLTFLoader) {
          window.removeEventListener('threeJSReady', handleThreeJSReady);
          console.log('✅ THREE.js libraries ready for 3D viewer');
          resolve();
        }
      };
      
      window.addEventListener('threeJSReady', handleThreeJSReady);
      
      // Set a reasonable timeout (5 seconds instead of 10)
      setTimeout(() => {
        window.removeEventListener('threeJSReady', handleThreeJSReady);
        if (!window.THREE || !window.GLTFLoader) {
          reject(new Error('THREE.js libraries failed to load within timeout'));
        }
      }, 5000);
    });
  }

  setupScene() {
    const rect = this.container.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;

    // Scene
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0xe8e8e8);

    // Camera
    this.camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 1000);
    this.camera.position.set(0, 0, 5.7);

    // Renderer
    this.renderer = new THREE.WebGLRenderer({ 
      canvas: this.canvas,
      antialias: true,
      alpha: true 
    });
    this.renderer.setSize(width, height);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.2;

    // Lighting
    const keyLight = new THREE.DirectionalLight(0xfff4e6, 1.4);
    keyLight.position.set(3, 4, 3);
    keyLight.castShadow = true;
    keyLight.shadow.mapSize.width = 2048;
    keyLight.shadow.mapSize.height = 2048;
    this.scene.add(keyLight);

    const fillLight = new THREE.DirectionalLight(0xe6f3ff, 0.6);
    fillLight.position.set(-3, 2, 2);
    this.scene.add(fillLight);

    const rimLight = new THREE.DirectionalLight(0xffffff, 0.4);
    rimLight.position.set(0, -2, -3);
    this.scene.add(rimLight);

    const ambientLight = new THREE.AmbientLight(0xf0f8ff, 0.3);
    this.scene.add(ambientLight);
  }

  async loadModel() {
    try {
      // Pre-load file format validation with AR support
      console.log(`🔍 Validating model file format: ${this.modelUrl}`);
      const validationResult = await this.validateModelFile(this.modelUrl);
      
      if (!validationResult.isValid) {
        throw new Error(validationResult.error);
      }
      
      console.log(`✅ File format validated: ${validationResult.detectedFormat}`);
      
      // Handle USDZ files - skip web viewer loading but enable AR
      if (validationResult.skipWebViewer && validationResult.detectedFormat === 'usdz') {
        console.log(`📱 USDZ file detected - skipping 3D web viewer, enabling AR only`);
        
        // Hide loading element
        if (this.loadingElement) {
          this.loadingElement.style.display = 'none';
        }
        
        // Show AR-only message instead of 3D viewer
        this.showAROnlyMessage();
        
        // Still add AR button - it should work with USDZ
        this.addCustomARButton();
        
        return Promise.resolve(); // Skip 3D loading but don't error
      }
      
      return new Promise((resolve, reject) => {
        const loader = new window.GLTFLoader();
        
        loader.load(
          this.modelUrl,
          (gltf) => {
            try {
              this.model = gltf.scene;
              
              if (!this.model) {
                throw new Error('Model dosyası geçersiz veya boş');
              }

              // Validate model has geometry
              let hasMesh = false;
              this.model.traverse((child) => {
                if (child.isMesh && child.geometry) {
                  hasMesh = true;
                }
              });

              if (!hasMesh) {
                throw new Error('Model dosyasında 3D geometri bulunamadı');
              }
              
              // Center and scale model
              const box = new THREE.Box3().setFromObject(this.model);
              const center = box.getCenter(new THREE.Vector3());
              const size = box.getSize(new THREE.Vector3());
              
              const maxDim = Math.max(size.x, size.y, size.z);
              if (maxDim === 0) {
                throw new Error('Model boyutları geçersiz');
              }
              
              const scale = 3 / maxDim;
              this.model.scale.multiplyScalar(scale);
              this.model.position.sub(center.multiplyScalar(scale));
              
              // Enable shadows
              this.model.traverse((child) => {
                if (child.isMesh) {
                  child.castShadow = true;
                  child.receiveShadow = true;
                }
              });
              
              this.scene.add(this.model);
              console.log('✅ 3D model loaded successfully');
              resolve();
              
            } catch (error) {
              console.error('❌ Model processing error:', error);
              reject(new Error(`Model işleme hatası: ${error.message}`));
            }
          },
          (progress) => {
            if (this.loadingElement && progress.loaded && progress.total) {
              const percent = Math.round((progress.loaded / progress.total) * 100);
              const spinner = this.loadingElement.querySelector('.loading-spinner');
              if (spinner) {
                spinner.textContent = `🎭 Yükleniyor... ${percent}%`;
              }
            }
          },
          (error) => {
            console.error('❌ Model loading error:', error);
            
            // Enhanced error categorization
            const errorInfo = this.categorizeModelError(error);
            reject(new Error(errorInfo.message));
          }
        );
      });
    } catch (error) {
      throw error;
    }
  }

  // File format validation method with AR support
  async validateModelFile(url) {
    try {
      console.log(`📋 Starting file validation for: ${url}`);
      
      // Extract file extension
      const urlObj = new URL(url, window.location.href);
      const pathname = urlObj.pathname;
      const extension = pathname.split('.').pop()?.toLowerCase();
      
      console.log(`📄 Detected file extension: ${extension}`);
      
      // Check if extension is supported (now including USDZ for AR)
      const webViewerExtensions = ['gltf', 'glb'];
      const arExtensions = ['usdz'];
      const allSupportedExtensions = [...webViewerExtensions, ...arExtensions];
      
      if (!allSupportedExtensions.includes(extension)) {
        console.warn(`⚠️ Unsupported file extension: ${extension}`);
        return {
          isValid: false,
          error: `Desteklenmeyen dosya formatı: .${extension}. Desteklenen formatlar: .gltf, .glb (web 3D görüntüleme), .usdz (AR görüntüleme)`,
          detectedFormat: extension,
          errorType: 'UNSUPPORTED_EXTENSION'
        };
      }
      
      // Handle USDZ files specially for AR
      if (extension === 'usdz') {
        console.log(`✅ USDZ file detected - AR format validation`);
        return {
          isValid: true,
          detectedFormat: 'usdz',
          isAROnly: true,
          skipWebViewer: true,
          errorType: null,
          message: 'USDZ file ready for AR functionality'
        };
      }
      
      // For GLB/GLTF files, perform HEAD request to check file headers
      const headerValidation = await this.validateFileHeaders(url);
      
      if (!headerValidation.isValid) {
        return headerValidation;
      }
      
      return {
        isValid: true,
        detectedFormat: headerValidation.detectedFormat,
        fileSize: headerValidation.fileSize,
        isAROnly: false,
        skipWebViewer: false,
        errorType: null
      };
      
    } catch (error) {
      console.error('❌ File validation error:', error);
      return {
        isValid: false,
        error: `Dosya doğrulaması başarısız: ${error.message}`,
        detectedFormat: 'unknown',
        errorType: 'VALIDATION_FAILED'
      };
    }
  }

  // File header validation method
  async validateFileHeaders(url) {
    try {
      console.log(`🔍 Checking file headers for: ${url}`);
      
      // Fetch first few bytes to check file signature
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Range': 'bytes=0-15' // Get first 16 bytes for header inspection
        }
      });
      
      if (!response.ok) {
        if (response.status === 404) {
          return {
            isValid: false,
            error: 'Model dosyası bulunamadı (404)',
            errorType: 'FILE_NOT_FOUND'
          };
        } else if (response.status >= 500) {
          return {
            isValid: false,
            error: 'Sunucu hatası - model dosyasına erişilemiyor',
            errorType: 'SERVER_ERROR'
          };
        } else {
          return {
            isValid: false,
            error: `Dosya erişim hatası: HTTP ${response.status}`,
            errorType: 'ACCESS_ERROR'
          };
        }
      }
      
      const contentLength = response.headers.get('content-length');
      const contentType = response.headers.get('content-type') || '';
      
      console.log(`📊 File info - Size: ${contentLength}, Type: ${contentType}`);
      
      // Get the actual bytes
      const arrayBuffer = await response.arrayBuffer();
      const bytes = new Uint8Array(arrayBuffer);
      
      // Check file signatures
      const signature = this.detectFileSignature(bytes);
      
      console.log(`🔍 Detected file signature: ${signature.type} (${signature.description})`);
      
      // Validate based on signature
      if (signature.type === 'ZIP') {
        return {
          isValid: false,
          error: 'Model dosyası ZIP formatında - GLB veya GLTF dosyası gerekli. ZIP dosyasını açıp içindeki GLB/GLTF dosyasını kullanın.',
          detectedFormat: 'ZIP',
          errorType: 'INVALID_FORMAT_ZIP'
        };
      } else if (signature.type === 'UNKNOWN') {
        // Check content type as fallback
        if (contentType.includes('application/zip')) {
          return {
            isValid: false,
            error: 'Model dosyası ZIP formatında tespit edildi - GLB veya GLTF dosyası gerekli.',
            detectedFormat: 'ZIP',
            errorType: 'INVALID_FORMAT_ZIP'
          };
        } else if (!contentType.includes('model') && !contentType.includes('application/octet-stream') && !contentType.includes('application/json')) {
          return {
            isValid: false,
            error: `Geçersiz dosya tipi: ${contentType}. Model dosyası (.glb veya .gltf) gerekli.`,
            detectedFormat: contentType,
            errorType: 'INVALID_CONTENT_TYPE'
          };
        }
      } else if (signature.type !== 'GLB' && signature.type !== 'GLTF') {
        return {
          isValid: false,
          error: `Geçersiz model formatı: ${signature.type}. GLB veya GLTF dosyası gerekli.`,
          detectedFormat: signature.type,
          errorType: 'INVALID_FORMAT'
        };
      }
      
      return {
        isValid: true,
        detectedFormat: signature.type,
        fileSize: contentLength ? parseInt(contentLength) : null,
        signature: signature
      };
      
    } catch (error) {
      console.error('❌ Header validation error:', error);
      
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        return {
          isValid: false,
          error: 'Ağ bağlantı hatası - model dosyasına erişilemiyor',
          errorType: 'NETWORK_ERROR'
        };
      }
      
      return {
        isValid: false,
        error: `Dosya başlığı kontrol hatası: ${error.message}`,
        errorType: 'HEADER_CHECK_FAILED'
      };
    }
  }

  // File signature detection method
  detectFileSignature(bytes) {
    if (bytes.length < 4) {
      return { type: 'UNKNOWN', description: 'Insufficient data' };
    }
    
    // Check for ZIP signature (PK)
    if (bytes[0] === 0x50 && bytes[1] === 0x4B) {
      return {
        type: 'ZIP',
        description: 'ZIP Archive (starts with PK)',
        signature: 'PK'
      };
    }
    
    // Check for GLB signature (glTF binary)
    if (bytes[0] === 0x67 && bytes[1] === 0x6C && bytes[2] === 0x54 && bytes[3] === 0x46) {
      return {
        type: 'GLB',
        description: 'glTF Binary (.glb)',
        signature: 'glTF'
      };
    }
    
    // Check for GLTF (JSON format) - looks for opening brace
    if (bytes[0] === 0x7B) { // '{'
      return {
        type: 'GLTF',
        description: 'glTF JSON (.gltf)',
        signature: '{'
      };
    }
    
    // Check for other common formats
    if (bytes[0] === 0xFF && bytes[1] === 0xD8) {
      return { type: 'JPEG', description: 'JPEG Image', signature: 'FFD8' };
    }
    
    if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4E && bytes[3] === 0x47) {
      return { type: 'PNG', description: 'PNG Image', signature: '89504E47' };
    }
    
    return {
      type: 'UNKNOWN',
      description: `Unknown format (${Array.from(bytes.slice(0, 4)).map(b => b.toString(16).padStart(2, '0')).join(' ')})`,
      signature: Array.from(bytes.slice(0, 4)).map(b => b.toString(16).padStart(2, '0')).join('')
    };
  }

  // Enhanced error categorization
  categorizeModelError(error) {
    const message = error.message || error.toString();
    
    if (message.includes('404') || message.includes('Not Found')) {
      return {
        type: 'FILE_NOT_FOUND',
        message: 'Model dosyası bulunamadı',
        technical: message,
        fallback: true
      };
    }
    
    if (message.includes('network') || message.includes('fetch') || message.includes('NetworkError')) {
      return {
        type: 'NETWORK_ERROR',
        message: 'Ağ bağlantı hatası - model dosyası yüklenemedi',
        technical: message,
        fallback: true
      };
    }
    
    if (message.includes('parse') || message.includes('JSON') || message.includes('SyntaxError')) {
      return {
        type: 'PARSE_ERROR',
        message: 'Model dosyası bozuk veya geçersiz format - GLB/GLTF dosyası kontrol edin',
        technical: message,
        fallback: true
      };
    }
    
    if (message.includes('Unexpected token') && message.includes('PK')) {
      return {
        type: 'ZIP_FILE_ERROR',
        message: 'Model dosyası ZIP formatında - GLB veya GLTF dosyası gerekli',
        technical: message,
        fallback: true
      };
    }
    
    if (message.includes('CORS')) {
      return {
        type: 'CORS_ERROR',
        message: 'Dosya erişim hatası - model sunucusu ayarları',
        technical: message,
        fallback: true
      };
    }
    
    return {
      type: 'GENERAL_ERROR',
      message: `Model hatası: ${message}`,
      technical: message,
      fallback: true
    };
  }

  animate() {
    this.animationId = requestAnimationFrame(() => this.animate());

    // Auto-rotation with improved speed
    if (this.model) {
      this.model.rotation.y += 0.01; // Increased rotation speed for better visibility
    }

    this.renderer.render(this.scene, this.camera);
  }

  showError(message = '3D model yüklenemedi') {
    if (!this.loadingElement) return;
    
    // Enhanced error display with fallback options
    const isLibraryError = message.includes('THREE.js') || message.includes('GLTFLoader');
    const displayMessage = isLibraryError
      ? '⚠️ 3D sistem yüklenmedi'
      : `⚠️ ${message}`;
    
    // Log detailed error for debugging
    this.logError('MODEL_LOAD_ERROR', message, {
      modelUrl: this.modelUrl,
      productId: this.product?.id,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent
    });
    
    // Show fallback content with better UX
    this.loadingElement.innerHTML = `
      <div class="error-fallback-container" style="
        text-align: center;
        padding: 20px;
        background: rgba(255, 255, 255, 0.95);
        border-radius: 12px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        backdrop-filter: blur(8px);
      ">
        <div class="error-message" style="
          color: #FF3B30;
          font-size: 14px;
          margin-bottom: 16px;
          font-weight: 500;
        ">
          ${displayMessage}
        </div>
        ${this.createFallbackContent()}
      </div>
    `;
    
    // Try to show fallback image after a short delay
    setTimeout(() => {
      this.showFallbackImage();
    }, 500);
  }

  // Show AR-only message for USDZ files
  showAROnlyMessage() {
    if (!this.canvas) return;
    
    // Hide the canvas since we won't be rendering 3D content
    this.canvas.style.display = 'none';
    
    // Create AR-only message container
    const arOnlyContainer = document.createElement('div');
    arOnlyContainer.className = 'ar-only-message-container';
    arOnlyContainer.style.cssText = `
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      text-align: center;
      padding: 24px;
      background: rgba(255, 255, 255, 0.95);
      border-radius: 16px;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
      backdrop-filter: blur(12px);
      border: 1px solid rgba(255, 255, 255, 0.8);
      max-width: 280px;
      width: 90%;
    `;
    
    const productImage = this.product?.featured_image ||
                        this.product?.images?.[0]?.src ||
                        this.product?.media?.find(m => m.media_type === 'image')?.src;
    
    arOnlyContainer.innerHTML = `
      <div style="margin-bottom: 16px;">
        ${productImage ? `
          <div style="
            width: 120px;
            height: 120px;
            margin: 0 auto 12px;
            border-radius: 12px;
            overflow: hidden;
            box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
          ">
            <img src="${productImage}" alt="${this.product?.title || 'Ürün Görüntüsü'}"
                 style="
                   width: 100%;
                   height: 100%;
                   object-fit: cover;
                 ">
          </div>
        ` : `
          <div style="
            width: 120px;
            height: 120px;
            margin: 0 auto 12px;
            background: linear-gradient(135deg, #f8f9fa, #e9ecef);
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 48px;
            box-shadow: inset 0 2px 8px rgba(0, 0, 0, 0.05);
          ">
            📱
          </div>
        `}
      </div>
      <div style="margin-bottom: 8px;">
        <h3 style="
          margin: 0;
          font-size: 18px;
          font-weight: 600;
          color: #1a1a1a;
          font-family: -apple-system, BlinkMacSystemFont, sans-serif;
        ">
          📱 AR Modeli
        </h3>
      </div>
      <div style="
        font-size: 14px;
        color: #666;
        line-height: 1.5;
        margin-bottom: 16px;
      ">
        Bu model sadece AR (Artırılmış Gerçeklik)<br>
        görüntüleme için optimize edilmiştir.
      </div>
      <div style="
        font-size: 12px;
        color: #888;
        background: rgba(248, 249, 250, 0.8);
        padding: 8px 12px;
        border-radius: 8px;
        line-height: 1.4;
      ">
        <strong>📱 Mobil cihazlarda:</strong> "Ortamında Gör" butonuna tıklayın<br>
        <strong>💻 Masaüstü:</strong> QR kodu telefonunuzla tarayın
      </div>
    `;
    
    this.container.appendChild(arOnlyContainer);
  }

  // Create fallback content based on error type and available data
  createFallbackContent() {
    const productImage = this.product?.featured_image ||
                        this.product?.images?.[0]?.src ||
                        this.product?.media?.find(m => m.media_type === 'image')?.src;
    
    if (productImage) {
      return `
        <div style="margin-bottom: 12px;">
          <img src="${productImage}" alt="${this.product?.title || 'Ürün Görüntüsü'}"
               style="
                 max-width: 150px;
                 max-height: 150px;
                 border-radius: 8px;
                 object-fit: cover;
                 opacity: 0.8;
               ">
        </div>
        <div style="font-size: 12px; color: #666; line-height: 1.4;">
          3D model yüklenemedi<br>
          <small>Ürün görüntüsü gösteriliyor</small>
        </div>
      `;
    }
    
    return `
      <div style="
        width: 120px;
        height: 120px;
        margin: 0 auto 12px;
        background: #f8f9fa;
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 24px;
        opacity: 0.6;
      ">
        📦
      </div>
      <div style="font-size: 12px; color: #666;">
        3D model mevcut değil
      </div>
    `;
  }

  // Show fallback image when 3D model fails
  showFallbackImage() {
    const productImage = this.product?.featured_image ||
                        this.product?.images?.[0]?.src ||
                        this.product?.media?.find(m => m.media_type === 'image')?.src;
    
    if (productImage && this.canvas) {
      // Hide the canvas and show fallback image
      this.canvas.style.display = 'none';
      
      // Create fallback image element
      const fallbackImage = document.createElement('img');
      fallbackImage.src = productImage;
      fallbackImage.alt = this.product?.title || 'Ürün Görüntüsü';
      fallbackImage.className = 'fallback-product-image';
      fallbackImage.style.cssText = `
        width: 100%;
        height: 100%;
        object-fit: cover;
        border-radius: var(--media-radius, 8px);
        filter: opacity(0.9) grayscale(0.1);
        transition: filter 0.3s ease;
      `;
      
      // Add hover effect
      fallbackImage.addEventListener('mouseenter', () => {
        fallbackImage.style.filter = 'opacity(1) grayscale(0)';
      });
      
      fallbackImage.addEventListener('mouseleave', () => {
        fallbackImage.style.filter = 'opacity(0.9) grayscale(0.1)';
      });
      
      this.container.appendChild(fallbackImage);
      
      this.logInfo('FALLBACK_IMAGE_SHOWN', 'Showing fallback product image', {
        imageUrl: productImage,
        productTitle: this.product?.title
      });
    }
  }

  // Enhanced logging system
  logError(type, message, details = {}) {
    const logEntry = {
      level: 'ERROR',
      type,
      message,
      details,
      timestamp: new Date().toISOString(),
      source: '3D_VIEWER'
    };
    
    console.error(`🚫 [3D Viewer] ${type}:`, message, details);
    
    // Store in session for debugging
    this.storeLogEntry(logEntry);
    
    // Send to analytics if available
    if (typeof gtag !== 'undefined') {
      gtag('event', '3d_viewer_error', {
        error_type: type,
        error_message: message,
        model_url: details.modelUrl,
        product_id: details.productId
      });
    }
  }

  logWarning(type, message, details = {}) {
    const logEntry = {
      level: 'WARNING',
      type,
      message,
      details,
      timestamp: new Date().toISOString(),
      source: '3D_VIEWER'
    };
    
    console.warn(`⚠️ [3D Viewer] ${type}:`, message, details);
    this.storeLogEntry(logEntry);
  }

  logInfo(type, message, details = {}) {
    const logEntry = {
      level: 'INFO',
      type,
      message,
      details,
      timestamp: new Date().toISOString(),
      source: '3D_VIEWER'
    };
    
    console.log(`ℹ️ [3D Viewer] ${type}:`, message, details);
    this.storeLogEntry(logEntry);
  }

  // Store log entries for debugging
  storeLogEntry(logEntry) {
    try {
      const logs = JSON.parse(sessionStorage.getItem('3d_viewer_logs') || '[]');
      logs.push(logEntry);
      
      // Keep only last 50 entries
      if (logs.length > 50) {
        logs.splice(0, logs.length - 50);
      }
      
      sessionStorage.setItem('3d_viewer_logs', JSON.stringify(logs));
    } catch (e) {
      // Ignore storage errors
    }
  }

  // Get stored logs for debugging
  static getLogs() {
    try {
      return JSON.parse(sessionStorage.getItem('3d_viewer_logs') || '[]');
    } catch (e) {
      return [];
    }
  }

  // Clear stored logs
  static clearLogs() {
    try {
      sessionStorage.removeItem('3d_viewer_logs');
    } catch (e) {
      // Ignore storage errors
    }
  }

  destroy() {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
    if (this.renderer) {
      this.renderer.dispose();
      this.renderer = null;
    }
    if (this.scene) {
      this.scene.clear();
      this.scene = null;
    }
    this.model = null;
    this.camera = null;
    
    // Clean up AR button if it exists
    const arButton = this.container.querySelector('.custom-ar-button');
    if (arButton && arButton.parentNode) {
      arButton.remove();
    }
  }

  // Method to check if container has required elements
  static hasRequiredElements(container) {
    const canvas = container.querySelector('.viewer-3d-canvas') ||
                   container.querySelector('.3d-viewer-canvas') ||
                   container.querySelector('.product-modal__3d-canvas') ||
                   container.querySelector('canvas');
    
    const loading = container.querySelector('.viewer-3d-loading') ||
                    container.querySelector('.3d-viewer-loading') ||
                    container.querySelector('.product-modal__3d-loading');
    
    return !!(canvas && loading);
  }

  // Static method to create proper container structure
  static createContainer(parentElement, options = {}) {
    const container = document.createElement('div');
    container.className = 'custom-3d-viewer-container';
    container.style.cssText = `
      position: relative;
      width: 100%;
      height: 100%;
      aspect-ratio: 1/1;
    `;

    const canvas = document.createElement('canvas');
    canvas.className = 'viewer-3d-canvas';
    canvas.style.cssText = `
      width: 100% !important;
      height: 100% !important;
      display: block;
      border-radius: var(--media-radius, 8px);
    `;

    const loading = document.createElement('div');
    loading.className = 'viewer-3d-loading';
    loading.style.cssText = `
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      z-index: 10;
    `;

    const spinner = document.createElement('div');
    spinner.className = 'loading-spinner';
    spinner.textContent = '🎭 3D Model Yükleniyor...';
    spinner.style.cssText = `
      color: #666;
      font-size: 14px;
      text-align: center;
      font-family: -apple-system, BlinkMacSystemFont, sans-serif;
    `;

    loading.appendChild(spinner);
    container.appendChild(canvas);
    container.appendChild(loading);
    
    if (parentElement) {
      parentElement.appendChild(container);
    }

    return container;
  }

  // Method to resize viewer (useful for responsive design)
  resize() {
    if (!this.renderer || !this.camera) return;

    const rect = this.container.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;

    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height);
  }

  // Method to pause/resume animation
  pause() {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
  }

  resume() {
    if (!this.animationId && this.renderer && this.scene && this.camera) {
      this.animate();
    }
  }
}

// Make available globally
window.Custom3DViewer = Custom3DViewer;
